import type { ReactNode } from "react";
import { useGlassSurface } from "@/lib/ground";
import { LocaleSwitcher, useT } from "../../i18n";
import { ThemeSwitcher } from "./theme-switcher";

/**
 * The door pages: the staff gate and its "wrong link", `/login`, `/invite`,
 * the `/super` sign-in, a club's or a team's gate, "no such tournament" and
 * the page nobody registered.
 *
 * One floating pane of glass on the public ground (board redesign, round
 * 10), the same pill the board's header is: the eyebrow says which door
 * this is, the wordmark is the way out to the list of tournaments, the
 * language and theme switchers are at the bottom because a first-ever
 * visitor arrives here with no way to have chosen either. It used to be a
 * light card with the dark band across its head, which was the one light
 * thing left between a QR code and the board.
 *
 * The ground is the board's: `useGlassSurface` puts `ground` (and `dark`,
 * unless the device chose the light theme) on <html> while a door is open,
 * and the classes leave with the last glass surface (`src/lib/ground.ts`).
 *
 * Its own small module rather than a corner of `KeyGate.tsx`: "no such
 * tournament" lives in the main chunk, and `KeyGate` brings the whole
 * session machinery with it.
 */
export function GateShell({ eyebrow, children }: { eyebrow: string; children: ReactNode }) {
  useGlassSurface();
  const t = useT();
  return (
    <div className="relative flex min-h-dvh items-center justify-center p-4">
      <div className="board-ground" aria-hidden="true" />
      <div className="glass board-float board-enter animate-in fade-in-0 slide-in-from-bottom-2 relative w-full max-w-sm text-foreground duration-300">
        <div className="flex items-baseline justify-between gap-4 border-b px-5 py-4">
          <span className="eyebrow text-muted-foreground">{eyebrow}</span>
          <a
            href="/"
            className="nxt text-xl leading-none text-brand-ink transition-opacity hover:opacity-80 dark:text-brand"
            title={t("board.allTournaments")}
          >
            {t("board.title")}
          </a>
        </div>
        <div className="flex flex-col gap-4 p-5">
          {children}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <LocaleSwitcher />
            <ThemeSwitcher />
          </div>
        </div>
      </div>
    </div>
  );
}
